import React from 'react';

/**
 * النجمة الثمانية الإسلامية (Rub el Hizb / Islamic 8-Point Star)
 * رمز الحزب والآية في المصاحف الشريفة
 */
export function IslamicStarMedallion({
  children,
  className = 'w-10 h-10',
  variant = 'gold', // 'gold' | 'emerald' | 'outline' | 'inactive'
  size = 40,
}) {
  const getColors = () => {
    switch (variant) {
      case 'emerald':
        return {
          fill: '#0d382c',
          stroke: '#c59b27',
          innerFill: '#175240',
          textColor: '#ffffff',
        };
      case 'gold':
        return {
          fill: '#c59b27',
          stroke: '#f4e095',
          innerFill: '#ab8219',
          textColor: '#ffffff',
        };
      case 'outline':
        return {
          fill: 'transparent',
          stroke: '#c59b27',
          innerFill: 'transparent',
          textColor: 'currentColor',
        };
      case 'inactive':
      default:
        return {
          fill: 'rgba(150, 160, 155, 0.15)',
          stroke: 'rgba(150, 160, 155, 0.4)',
          innerFill: 'transparent',
          textColor: 'currentColor',
        };
    }
  };

  const colors = getColors();

  return (
    <div className={`relative inline-flex items-center justify-center select-none ${className}`}>
      <svg
        viewBox="0 0 100 100"
        className="w-full h-full drop-shadow-xs transition-transform duration-300"
        aria-hidden="true"
      >
        {/* Outer 8-pointed star made of two rotated rounded squares */}
        <rect
          x="14"
          y="14"
          width="72"
          height="72"
          rx="8"
          fill={colors.fill}
          stroke={colors.stroke}
          strokeWidth="3.5"
        />
        <rect
          x="14"
          y="14"
          width="72"
          height="72"
          rx="8"
          transform="rotate(45 50 50)"
          fill={colors.fill}
          stroke={colors.stroke}
          strokeWidth="3.5"
        />

        {/* Inner concentric ring */}
        <circle
          cx="50"
          cy="50"
          r="28"
          fill={colors.innerFill}
          stroke={colors.stroke}
          strokeWidth="2"
          strokeDasharray="2 3"
        />
        <circle
          cx="50"
          cy="50"
          r="23"
          fill="none"
          stroke={colors.stroke}
          strokeWidth="1.2"
        />
      </svg>

      {/* Center content / number / icon */}
      <div
        className="absolute inset-0 flex items-center justify-center font-bold text-center z-10"
        style={{ color: colors.textColor }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * إطار ترويسة السورة القرآنية الكلاسيكي (Classical Surah Header Frame)
 * مستوحى من الإطارات المذهبة في فواتح السور في المصحف الشريف
 */
export function SurahHeaderRibbon({
  title,
  subtitle,
  orderNumber,
  badgeText = null,
  rightAction = null,
  className = '',
}) {
  return (
    <div
      className={`relative w-full rounded-2xl sm:rounded-3xl p-4 sm:p-5 border-2 border-[#c59b27] dark:border-[#d8b24a] bg-gradient-to-r from-[#fbf9f2] via-[#fffefc] to-[#fbf9f2] dark:from-[#0d231b] dark:via-[#122e24] dark:to-[#0d231b] shadow-md shadow-[#c59b27]/10 ${className}`}
    >
      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3.5 min-w-0">
          {orderNumber && (
            <IslamicStarMedallion
              variant="emerald"
              className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 text-sm sm:text-base font-black font-amiri"
            >
              {orderNumber}
            </IslamicStarMedallion>
          )}

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {subtitle && (
                <span className="text-[0.72rem] sm:text-xs font-bold text-[#c59b27] dark:text-[#d8b24a] tracking-wider font-amiri">
                  {subtitle}
                </span>
              )}
              {badgeText && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.68rem] font-bold bg-[#e8f5ee] dark:bg-emerald-950/50 text-[#0d382c] dark:text-emerald-300 border border-[#c4e6d4] dark:border-emerald-800/40">
                  {badgeText}
                </span>
              )}
            </div>

            <h3 className="font-extrabold text-[#0d382c] dark:text-[#f7f4ea] text-lg sm:text-xl md:text-2xl truncate leading-snug font-amiri">
              {title}
            </h3>
          </div>
        </div>

        {rightAction && (
          <div className="shrink-0 ms-auto flex items-center justify-end">
            {rightAction}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * فاصل إسلامي مزخرف (Ornamental Ayah & Section Divider)
 */
export function AyahDivider({ className = 'my-4' }) {
  return (
    <div className={`w-full flex items-center justify-center gap-3 text-[#c59b27] dark:text-[#d8b24a] select-none ${className}`}>
      <div className="flex-1 h-px bg-gradient-to-l from-[#c59b27]/60 via-[#c59b27]/20 to-transparent" />
      <div className="w-5 h-5 flex items-center justify-center">
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 opacity-80">
          <path d="M12 2l2.4 4.8 5.3.8-3.8 3.7.9 5.2-4.8-2.5-4.8 2.5.9-5.2-3.8-3.7 5.3-.8L12 2z" />
        </svg>
      </div>
      <div className="flex-1 h-px bg-gradient-to-r from-[#c59b27]/60 via-[#c59b27]/20 to-transparent" />
    </div>
  );
}

/**
 * البسملة الشريفة بخط أميري أنيق
 */
export function BismillahCalligraphy({ className = 'my-2 text-center' }) {
  return (
    <div className={`font-amiri text-lg sm:text-xl font-bold text-[#c59b27] dark:text-[#d8b24a] tracking-wide select-none ${className}`}>
      بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
    </div>
  );
}
